import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pencil, Loader2 } from "lucide-react";

export interface ProductIdentitySectionProps {
  description: string;
  domain: string;
  industry: string;
  onSave: (data: {
    description: string;
    domain?: string;
    industry?: string;
  }) => void;
  canEdit: boolean;
  isPending: boolean;
}

export function ProductIdentitySection({
  description,
  domain,
  industry,
  onSave,
  canEdit,
  isPending,
}: ProductIdentitySectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [descValue, setDescValue] = useState(description);
  const [domainValue, setDomainValue] = useState(domain);
  const [industryValue, setIndustryValue] = useState(industry);

  useEffect(() => {
    setDescValue(description);
    setDomainValue(domain);
    setIndustryValue(industry);
  }, [description, domain, industry]);

  const hasChanges =
    descValue.trim() !== description.trim() ||
    domainValue.trim() !== domain.trim() ||
    industryValue.trim() !== industry.trim();

  function handleSave() {
    onSave({
      description: descValue.trim(),
      domain: domainValue.trim() || undefined,
      industry: industryValue.trim() || undefined,
    });
    setIsEditing(false);
  }

  function handleCancel() {
    setDescValue(description);
    setDomainValue(domain);
    setIndustryValue(industry);
    setIsEditing(false);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">
              Product overview & problem space
            </CardTitle>
            <CardDescription className="mt-1">
              The core narrative, domain scope, and industry that ground all
              walkthrough runners and scenario generators.
            </CardDescription>
          </div>
          {canEdit && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="identity-domain">Domain</Label>
                <Input
                  id="identity-domain"
                  value={domainValue}
                  onChange={(e) => setDomainValue(e.target.value)}
                  placeholder="e.g. Swedish workplace safety management"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  The specific problem space — not what software does, but what
                  real-world process it serves.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="identity-industry">Industry</Label>
                <Input
                  id="identity-industry"
                  value={industryValue}
                  onChange={(e) => setIndustryValue(e.target.value)}
                  placeholder="e.g. Construction & Heavy Industry"
                />
                <p className="text-xs text-muted-foreground">
                  The sector your users operate in. Influences which regulatory
                  standards apply.
                </p>
              </div>
            </div>

            <div className="space-y-2 pt-1 border-t border-border/50">
              <div className="flex items-center justify-between">
                <Label htmlFor="identity-description">
                  Product description & key user journeys
                </Label>
                <span className="text-xs text-muted-foreground">
                  Injected into every walkthrough runner
                </span>
              </div>
              <Textarea
                id="identity-description"
                value={descValue}
                onChange={(e) => setDescValue(e.target.value)}
                placeholder="What is this product, who is it for, and what are its key user journeys?"
                rows={5}
                className="text-sm leading-relaxed"
              />
              <p className="text-xs text-muted-foreground">
                Grounded summary explaining who uses the software, what journeys
                they complete, and operational context.
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={isPending || !hasChanges}
                onClick={handleSave}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-2 border-b border-border/50">
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Domain
                </span>
                <p className="text-sm font-medium text-foreground">
                  {domain || (
                    <span className="text-muted-foreground font-normal">
                      Not set
                    </span>
                  )}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Industry
                </span>
                <p className="text-sm font-medium text-foreground">
                  {industry || (
                    <span className="text-muted-foreground font-normal">
                      Not set
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  Product description & key user journeys
                </span>
                <span className="text-xs text-muted-foreground">
                  Injected into runner prompt
                </span>
              </div>
              <div className="rounded-md bg-muted/30 p-4">
                {description ? (
                  <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                    {description}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No description provided yet. Add a description so
                    walkthrough runners understand your product context and
                    business model.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
